import React, { useEffect, useState } from 'react';
import {
  CcfIconButton,
  CcfSwitchItem,
  CcfTypography,
  useTranslator,
} from '@nice-ccf/ui-controls';
import {
  Box,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CcfNotificationSettingsStyles from './ccf-avnotifications.styles';
import { NotificationSettings } from '@nice-ccf/core-sdk';
import { useDispatch, useSelector } from 'react-redux';
import { VolumeUp } from '@mui/icons-material';

export enum NotificationType {
  INCOMING_VOICE_INTERACTION = 'IncomingVoiceInteraction',
  INCOMING_DIGITAL_INTERACTION = 'IncomingDigitalInteraction',
  NEW_REPLY_DIGITAL_INTERACTION = 'newReplyDigitalInteraction',
  NEW_AGENT_MESSAGE = 'NewAgentMessage',
  END_INTERACTION = 'EndInteraction',
  END_DIGITAL_INTERACTION = 'EndDigitalInteraction',
  INCOMING_VOICEMAIL_INTERACTION = 'IncomingVoicemailInteraction',
  INCOMING_WORKITEM_INTERACTION = 'IncomingWorkitemInteraction',
}
/**
 * @example CcfAVNotifications()
 * @returns
 */
export function CcfAVNotifications() {
  const [translate] = useTranslator();
  const theme = useTheme();
  const isSmView = useMediaQuery(theme.breakpoints.down('xl'));
  const notificationSettingsStyles = CcfNotificationSettingsStyles(
    theme,
    isSmView
  );
  const tones = [
    { id: 1, fileName: 'agent-message', name: 'Tone 1' },
    { id: 2, fileName: 'new-contact', name: 'Tone 2' },
    { id: 3, fileName: 'new-chat-message', name: 'Tone 3' },
    { id: 4, fileName: 'end-contact', name: 'Tone 4' },
    { id: 5, fileName: 'ziptone_03', name: 'Tone 5' },
    { id: 6, fileName: 'ziptone_BambooBellArp', name: 'Tone 6' },
    { id: 7, fileName: 'ziptone_disconnect01', name: 'Tone 7' },
    { id: 8, fileName: 'ziptone_glassdancer3', name: 'Tone 8' },
    { id: 9, fileName: 'ziptone_glock4', name: 'Tone 9' },
    { id: 10, fileName: 'ziptone_quickClick', name: 'Tone 10' },
  ];
  const intAvNotifications = {
    audioAgentMessage: false,
    audioAgentMessageTone: 'agent-message',
    audioEndContact: false,
    audioEndContactTone: 'end-contact',
    audioNewContactReply: false,
    audioNewContactReplyTone: 'new-chat-message',
    audioNewContact: false,
    audioNewContactTone: 'new-contact',
    visualAgentMessage: false,
    visualEndContact: false,
    visualNewContactReply: false,
    visualNewContact: false,
  };
  const [avNotifications, setAVNotificationSettings] =
    useState(intAvNotifications);

  /**
   *
   * @param event - switch event
   * Callback fired when the state is changed.
   * @param checked - If `true`, the component appears selected.
   * @example handleChange
   *
   */
  const playAudioForNotification = (
    notificationType: string,
    audioFilePath: string,
    loop = true
  ) => {
    console.log('play');
  };

  /**
   *
   * @param event - switch event
   * Callback fired when the state is changed.
   * @param checked - If `true`, the component appears selected.
   * @example handleChange
   *
   */
  const handleChange = (
    event: React.SyntheticEvent<Element, Event>,
    checked: boolean
  ) => {
    const target = event.target as HTMLInputElement;
    const updatedAVNotificationSettings = {
      ...avNotifications,
      [target.name]: checked,
    };
    setAVNotificationSettings(updatedAVNotificationSettings);
  };

  /**
   *
   * @param event - switch event
   * Callback fired when the state is changed.
   * @param checked - If `true`, the component appears selected.
   * @example handleChange
   *
   */
  const handleToneChange = (event: SelectChangeEvent) => {
    const target = event.target;
    const updatedAVNotificationSettings = {
      ...avNotifications,
      [target.name]: target.value,
    };
    setAVNotificationSettings(updatedAVNotificationSettings);
  };

  const MenuProps = {
    PaperProps: {
      style: notificationSettingsStyles.selectMenu,
    },
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container px={2} sx={{ justifyContent: 'space-between' }}>
        <Grid
          item
          xl={6}
          pt={2}
          style={notificationSettingsStyles.settingsGrid}
        >
          <div style={notificationSettingsStyles.header}>
            <div>
              <CcfTypography sx={notificationSettingsStyles.headerText}>
                {translate('audioNotifications')}
              </CcfTypography>
            </div>
          </div>

          <Box sx={notificationSettingsStyles.toggle}>
            <CcfSwitchItem
              handleChange={handleChange}
              hideLessThanExtraLarge={false}
              isChecked={avNotifications.audioAgentMessage}
              name={NotificationSettings.AUDIO_AGENT_MESSAGE}
              text="newAgentMessage"
              listStyles={notificationSettingsStyles.listStyle}
              labelStyles={notificationSettingsStyles.labelStyle}
            />
            <div style={notificationSettingsStyles.tone}>
              <Select
                labelId="tone-select-label"
                id="audio-agent-tone-select"
                value={avNotifications.audioAgentMessageTone}
                name={NotificationSettings.AUDIO_AGENT_MESSAGE_TONE}
                onChange={handleToneChange}
                style={notificationSettingsStyles.select}
                MenuProps={MenuProps}
              >
                {tones.map((tone) => (
                  <MenuItem value={tone.fileName} key={tone.id}>
                    {tone.name}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <CcfIconButton
              onClick={() =>
                playAudioForNotification(
                  NotificationType.NEW_AGENT_MESSAGE,
                  avNotifications.audioAgentMessageTone
                )
              }
            >
              <VolumeUp />
            </CcfIconButton>
          </Box>

          <Box sx={notificationSettingsStyles.toggle}>
            <CcfSwitchItem
              handleChange={handleChange}
              hideLessThanExtraLarge={false}
              isChecked={avNotifications.audioNewContact}
              name={NotificationSettings.AUDIO_NEW_CONTACT}
              text="newContact"
              listStyles={notificationSettingsStyles.listStyle}
              labelStyles={notificationSettingsStyles.labelStyle}
            />
            <div style={notificationSettingsStyles.tone}>
              <Select
                labelId="tone-select-label"
                id="new-contacttone-select"
                value={avNotifications.audioNewContactTone}
                name={NotificationSettings.AUDIO_NEW_CONTACT_TONE}
                onChange={handleToneChange}
                style={notificationSettingsStyles.select}
                MenuProps={MenuProps}
              >
                {tones.map((tone) => (
                  <MenuItem value={tone.fileName} key={tone.id}>
                    {tone.name}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <CcfIconButton
              onClick={() =>
                playAudioForNotification(
                  NotificationType.INCOMING_VOICE_INTERACTION,
                  avNotifications.audioNewContactTone,
                  false
                )
              }
            >
              <VolumeUp />
            </CcfIconButton>
          </Box>
          <Box sx={notificationSettingsStyles.toggle}>
            <CcfSwitchItem
              handleChange={handleChange}
              hideLessThanExtraLarge={false}
              isChecked={avNotifications.audioNewContactReply}
              name={NotificationSettings.AUIDO_NEW_CONTACT_REPLY}
              text="newContactReply"
              listStyles={notificationSettingsStyles.listStyle}
              labelStyles={notificationSettingsStyles.labelStyle}
            />
            <div style={notificationSettingsStyles.tone}>
              <Select
                labelId="tone-select-label"
                id="new-contact-reply-tone-select"
                value={avNotifications.audioNewContactReplyTone}
                name={NotificationSettings.AUIDO_NEW_CONTACT_REPLY_TONE}
                onChange={handleToneChange}
                style={notificationSettingsStyles.select}
                MenuProps={MenuProps}
              >
                {tones.map((tone) => (
                  <MenuItem value={tone.fileName} key={tone.id}>
                    {tone.name}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <CcfIconButton
              onClick={() =>
                playAudioForNotification(
                  NotificationType.NEW_REPLY_DIGITAL_INTERACTION,
                  avNotifications.audioNewContactReplyTone
                )
              }
            >
              <VolumeUp />
            </CcfIconButton>
          </Box>
          <Box sx={notificationSettingsStyles.toggle}>
            <CcfSwitchItem
              handleChange={handleChange}
              hideLessThanExtraLarge={false}
              isChecked={avNotifications.audioEndContact}
              name={NotificationSettings.AUDIO_END_CONTACT}
              text="endChatOrCall"
              listStyles={notificationSettingsStyles.listStyle}
              labelStyles={notificationSettingsStyles.labelStyle}
            />
            <div style={notificationSettingsStyles.tone}>
              <Select
                labelId="tone-select-label"
                id="end-contact-tone-select"
                value={avNotifications.audioEndContactTone}
                name={NotificationSettings.AUDIO_END_CONTACT_TONE}
                onChange={handleToneChange}
                style={notificationSettingsStyles.select}
                MenuProps={MenuProps}
              >
                {tones.map((tone) => (
                  <MenuItem value={tone.fileName} key={tone.id}>
                    {tone.name}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <CcfIconButton
              id="audioEndContactToneIcon"
              onClick={() => {
                playAudioForNotification(
                  NotificationType.END_INTERACTION,
                  avNotifications.audioEndContactTone
                );
              }}
            >
              <VolumeUp />
            </CcfIconButton>
          </Box>
        </Grid>
        <Grid
          item
          xl={6}
          pt={2}
          style={notificationSettingsStyles.settingsGrid}
        >
          <div style={notificationSettingsStyles.header}>
            <div>
              <CcfTypography sx={notificationSettingsStyles.headerText}>
                {translate('visualNotifications')}
              </CcfTypography>
            </div>
          </div>
          <Box sx={notificationSettingsStyles.voicetoggle}>
            <CcfSwitchItem
              handleChange={handleChange}
              hideLessThanExtraLarge={false}
              isChecked={avNotifications.visualAgentMessage}
              name={NotificationSettings.VISUAL_AGENT_MESSAGE}
              text="newAgentMessage"
              listStyles={notificationSettingsStyles.listStyle}
              labelStyles={notificationSettingsStyles.labelStyle}
            />
          </Box>

          <Box sx={notificationSettingsStyles.voicetoggle}>
            <CcfSwitchItem
              handleChange={handleChange}
              hideLessThanExtraLarge={false}
              isChecked={avNotifications.visualNewContact}
              name={NotificationSettings.VISUAL_NEW_CONTACT}
              text="newContact"
              listStyles={notificationSettingsStyles.listStyle}
              labelStyles={notificationSettingsStyles.labelStyle}
            />
          </Box>

          <Box sx={notificationSettingsStyles.voicetoggle}>
            <CcfSwitchItem
              handleChange={handleChange}
              hideLessThanExtraLarge={false}
              isChecked={avNotifications.visualNewContactReply}
              name={NotificationSettings.VISUAL_NEW_CONTACT_REPLY}
              text="newContactReply"
              listStyles={notificationSettingsStyles.listStyle}
              labelStyles={notificationSettingsStyles.labelStyle}
            />
          </Box>

          <Box sx={notificationSettingsStyles.voicetoggle}>
            <CcfSwitchItem
              handleChange={handleChange}
              hideLessThanExtraLarge={false}
              isChecked={avNotifications.visualEndContact}
              name={NotificationSettings.VISUAL_END_CONTACT}
              text="endChatOrCall"
              listStyles={notificationSettingsStyles.listStyle}
              labelStyles={notificationSettingsStyles.labelStyle}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default CcfAVNotifications;
